/**
 * Cart drawer analytics — Elevar schema.
 *
 * Driven entirely by the `brighton:cart:*` events the store already emits, so no component
 * contains tracking code and deleting this file removes all tracking.
 *
 * WHY ELEVAR AND NOT STANDARD GA4 NAMES: this store's GA4 pipeline is Elevar feeding GTM
 * container GTM-5HL4NMB. Verified on production — every dataLayer event is `dl_`-prefixed
 * (`dl_user_data`, `dl_view_item`) and there are ZERO standard GA4 event names. A container
 * configured for Elevar's schema would silently ignore `add_to_cart`, so emitting GA4-standard
 * names would produce no reporting at all.
 *
 * Events are pushed to `window.ElevarDataLayer` when present rather than `window.dataLayer`:
 * Elevar enriches from there, adding `event_id`, `event_time`, `event_state` and
 * `user_properties`. Pushing straight to dataLayer skips that enrichment.
 *
 * Product shape copied from a real `dl_view_item` payload on production:
 *   { id (barcode), name, brand, category, variant, price (STRING 2dp), list,
 *     product_id, variant_id, compare_at_price (STRING 2dp), image, quantity }
 *
 * No module system in assets/ — attaches window.BrightonCartAnalytics.
 */
(function () {
  if (window.BrightonCartAnalytics) return;

  const LIST_NAME = 'Cart Drawer';

  /**
   * Elevar's Shopify Web Pixel (dl-web-pixel.js) already emits dl_add_to_cart and
   * dl_remove_from_cart for ANY Cart AJAX call, because Shopify's Web Pixels API fires
   * product_added_to_cart / product_removed_from_cart on every /cart/add.js and /cart/change.js.
   * Emitting them here as well produced two of each per interaction — confirmed in the dataLayer.
   *
   * dl_begin_checkout is likewise covered by Elevar's checkout_started subscription.
   *
   * dl_view_cart is NOT covered: Elevar fires it on the /cart page, and with the drawer live that
   * page is never visited, so opening the drawer is the only signal a cart was viewed. That one
   * stays on.
   *
   * Off by default. Turn on only if Elevar's pixel is disabled or its coverage changes, and check
   * for double-counting when you do.
   */
  const readAnalyticsConfig = () => {
    try {
      const el = document.getElementById('cart-drawer-config');
      const config = el ? JSON.parse(el.textContent) : null;
      return (config && config.analytics) || {};
    } catch (e) {
      return {};
    }
  };

  const analyticsConfig = readAnalyticsConfig();
  const emitMutationEvents = analyticsConfig.emitMutationEvents === true;

  /** Elevar formats money as a 2dp string, not a number. */
  const money = (cents) => ((Number(cents) || 0) / 100).toFixed(2);

  /**
   * Elevar's own queue when available, so it can enrich the event. Falls back to dataLayer so
   * events are still observable if Elevar hasn't booted (or is blocked by consent).
   */
  const push = (payload) => {
    if (window.ElevarDataLayer && typeof window.ElevarDataLayer.push === 'function') {
      window.ElevarDataLayer.push(payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /** Maps a drawer line item to Elevar's product shape. */
  const toProduct = (line, quantityOverride, position) => ({
    id: line.barcode || String(line.variantId),
    name: line.title,
    brand: line.vendor || '',
    category: line.productType || '',
    variant: line.variantTitle || '',
    price: money(line.unitPrice),
    compare_at_price: money(line.compareAtPrice),
    list: LIST_NAME,
    product_id: String(line.productId),
    variant_id: String(line.variantId),
    image: line.image || '',
    quantity: String(quantityOverride !== undefined ? quantityOverride : line.quantity),
    position: position !== undefined ? position + 1 : undefined,
  });

  /** Recommendations carry a formatted price range, so numeric price is left blank. */
  const toRecProduct = (rec, index) => ({
    id: String(rec.productId),
    name: rec.title,
    brand: '',
    category: '',
    variant: '',
    price: '',
    list: LIST_NAME,
    product_id: String(rec.productId),
    variant_id: '',
    image: rec.image || '',
    quantity: '1',
    position: index + 1,
  });

  const cartOf = (d) => (d && d.cart) || {};
  const currencyOf = (d) => cartOf(d).currencyCode || 'USD';
  const visibleLines = (d) => (cartOf(d).items || []).filter((i) => !i.flags || !i.flags.hidden);

  const on = (name, handler) =>
    document.addEventListener(`brighton:cart:${name}`, (e) => {
      try {
        handler(e.detail || {});
      } catch (err) {
        // Analytics must never break the cart.
        if (window.console) console.warn(`[cart-analytics] ${name} handler failed`, err);
      }
    });

  /* ------------------------------------------------------------------ cart */

  on('open', (d) => {
    push({
      event: 'dl_view_cart',
      ecommerce: {
        currencyCode: currencyOf(d),
        actionField: { list: LIST_NAME },
        impressions: visibleLines(d).map((l, i) => toProduct(l, undefined, i)),
      },
      cart_total: money(cartOf(d).totalPrice),
    });
  });

  on('add', (d) => {
    if (!emitMutationEvents) return;
    const requested = (d.items || []).map((i) => String(i.id));
    const lines = visibleLines(d).filter((l) => requested.indexOf(String(l.variantId)) !== -1);
    if (!lines.length) return;
    push({
      event: 'dl_add_to_cart',
      ecommerce: {
        currencyCode: currencyOf(d),
        add: { actionField: { list: LIST_NAME }, products: lines.map((l) => toProduct(l)) },
      },
    });
  });

  /**
   * Quantity steppers report the DELTA, not the resulting quantity — a stepper taking a line
   * from 2 to 3 is one unit added, and reporting 3 would inflate every metric downstream.
   */
  on('line-item-increase', (d) => {
    if (!emitMutationEvents || !d.item) return;
    const delta = Math.max(1, (d.quantity || 0) - (d.previousQuantity || 0));
    push({
      event: 'dl_add_to_cart',
      ecommerce: {
        currencyCode: currencyOf(d),
        add: { actionField: { list: LIST_NAME }, products: [toProduct(d.item, delta)] },
      },
    });
  });

  on('line-item-decrease', (d) => {
    if (!emitMutationEvents || !d.item) return;
    const delta = Math.max(1, (d.previousQuantity || 0) - (d.quantity || 0));
    push({
      event: 'dl_remove_from_cart',
      ecommerce: {
        currencyCode: currencyOf(d),
        remove: { actionField: { list: LIST_NAME }, products: [toProduct(d.item, delta)] },
      },
    });
  });

  on('line-item-removed', (d) => {
    if (!emitMutationEvents || !d.item) return;
    const qty = d.previousQuantity || d.item.quantity || 1;
    push({
      event: 'dl_remove_from_cart',
      ecommerce: {
        currencyCode: currencyOf(d),
        remove: { actionField: { list: LIST_NAME }, products: [toProduct(d.item, qty)] },
      },
    });
  });

  on('checkout', (d) => {
    if (!emitMutationEvents) return;
    push({
      event: 'dl_begin_checkout',
      ecommerce: {
        currencyCode: currencyOf(d),
        checkout: {
          actionField: { step: '1', action: 'checkout' },
          products: visibleLines(d).map((l, i) => toProduct(l, undefined, i)),
        },
      },
      cart_total: money(cartOf(d).totalPrice),
    });
  });

  /* ------------------------------------------------------ recommendations */

  on('recommendations-view', (d) => {
    if (!d.products || !d.products.length) return;
    push({
      event: 'dl_view_item_list',
      ecommerce: {
        currencyCode: 'USD',
        impressions: d.products.map((r, i) => toRecProduct(r, i)),
      },
      item_list_id: d.listId,
      item_list_name: d.listName,
    });
  });

  on('recommendation-click', (d) => {
    if (!d.product) return;
    push({
      event: 'dl_select_item',
      ecommerce: {
        currencyCode: 'USD',
        click: {
          actionField: { list: d.listName || LIST_NAME, action: 'click' },
          products: [toRecProduct(d.product, d.index || 0)],
        },
      },
      item_list_id: d.listId,
    });
  });

  /* ---------------------------------------------- Brighton-specific events */

  /**
   * No Elevar equivalent exists for these, so they keep the dl_ prefix for consistency with
   * everything else in the container but are custom to this cart.
   */
  on('close', (d) => push({ event: 'dl_cart_drawer_close', cart_total: money(cartOf(d).totalPrice) }));
  on('gift-wrap-applied', (d) => push({ event: 'dl_gift_wrap_applied', variant_id: String(d.variantId || '') }));
  on('gift-wrap-removed', () => push({ event: 'dl_gift_wrap_removed' }));
  on('save-for-later', (d) => push({
    event: 'dl_save_for_later',
    product_id: String(d.productId || ''),
    variant_id: String(d.variantId || ''),
    name: d.title || '',
  }));
  on('reconciled', (d) => push({ event: 'dl_cart_auto_adjusted', reason: d.reason }));

  window.BrightonCartAnalytics = { push, toProduct, toRecProduct };
})();
